import React, { useState } from 'react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import Icon from '../../../components/AppIcon';

const ChildProfileForm = ({ onSubmit, onCancel }) => {
  const [formData, setFormData] = useState({
    nickname: '',
    sexAtBirth: '',
    dateOfBirth: '',
    sportTags: []
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const sportOptions = [
    { value: 'football', label: 'Football' },
    { value: 'basketball', label: 'Basketball' },
    { value: 'swimming', label: 'Swimming' },
    { value: 'athletics', label: 'Athletics' },
    { value: 'tennis', label: 'Tennis' },
    { value: 'rugby', label: 'Rugby' },
    { value: 'cricket', label: 'Cricket' },
    { value: 'gymnastics', label: 'Gymnastics' },
    { value: 'cycling', label: 'Cycling' },
    { value: 'dance', label: 'Dance' },
    { value: 'martial_arts', label: 'Martial Arts' },
    { value: 'other', label: 'Other' }
  ];

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.nickname?.trim()) {
      newErrors.nickname = 'Nickname or initials are required';
    }

    if (!formData?.sexAtBirth) {
      newErrors.sexAtBirth = 'Sex at birth is required';
    }

    if (!formData?.dateOfBirth) {
      newErrors.dateOfBirth = 'Date of birth is required';
    } else {
      const dob = new Date(formData?.dateOfBirth);
      const today = new Date();
      const age = (today - dob) / (1000 * 60 * 60 * 24 * 365.25);
      
      if (age < 6 || age > 18) {
        newErrors.dateOfBirth = 'Child must be between 6 and 18 years old';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    if (!validateForm()) return;

    setIsSubmitting(true);
    try {
      await onSubmit(formData);
    } catch (err) {
      console.error('Error submitting form:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto">
      <div className="bg-card border border-border rounded-lg p-8 shadow-sm">
        <div className="flex items-center mb-6">
          <div className="bg-primary/10 p-4 rounded-full mr-4">
            <Icon name="UserPlus" className="w-8 h-8 text-primary" />
          </div>
          <h1 className="text-2xl font-bold text-text-primary">
            Create Child Profile
          </h1>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <Input
            label="Nickname or Initials"
            type="text"
            value={formData?.nickname}
            onChange={(e) => setFormData({ ...formData, nickname: e?.target?.value })}
            error={errors?.nickname}
            placeholder="e.g., Emma or E.J."
            required
            description="This will be used to identify the child in the app"
          />

          <Select
            label="Sex at Birth"
            options={[
              { value: 'female', label: 'Female' },
              { value: 'male', label: 'Male' }
            ]}
            value={formData?.sexAtBirth}
            onChange={(value) => setFormData({ ...formData, sexAtBirth: value })}
            error={errors?.sexAtBirth}
            placeholder="Select sex at birth"
            required
            description="Required for accurate PHV estimation"
          />

          <Input
            label="Date of Birth"
            type="date"
            value={formData?.dateOfBirth}
            onChange={(e) => setFormData({ ...formData, dateOfBirth: e?.target?.value })}
            error={errors?.dateOfBirth}
            required
            description="Used to calculate age for growth analysis"
          />

          <Select
            label="Sport Contexts (Optional)"
            options={sportOptions}
            value={formData?.sportTags}
            onChange={(value) => setFormData({ ...formData, sportTags: value })}
            placeholder="Select sports or activities"
            multiple
            searchable
            description="Tags only - no sensitive health data stored"
          />

          <div className="bg-info/10 border border-info rounded-lg p-4">
            <div className="flex items-start">
              <Icon name="Info" className="w-5 h-5 text-info mr-3 mt-0.5 flex-shrink-0" />
              <div className="text-sm text-text-secondary">
                <p className="font-medium text-text-primary mb-1">Privacy Note</p>
                <p>Only essential information is stored. No sensitive health data or location information is collected.</p>
              </div>
            </div>
          </div>

          <div className="flex justify-between pt-4">
            <Button 
              type="button"
              onClick={onCancel}
              variant="outline"
              iconName="X"
            >
              Cancel
            </Button>
            <Button 
              type="submit"
              loading={isSubmitting}
              iconName="Check"
            >
              Create Profile
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ChildProfileForm;